import { cx } from 'class-variance-authority';
import Image from 'next/image';
import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isSideways, ROTATION_CLASS_NAME, type Rotation } from '@/utils/rotation';

type BaseImageProps = {
  alt: string;
  src: string;
  rotation?: Rotation;
};

const BaseImage = forwardRef<HTMLImageElement, BaseImageProps>(({ alt, src, rotation = 0 }, ref) => {
  const { t } = useTranslation('entity-details', { keyPrefix: 'fieldset.documents.details' });
  const [hasError, setHasError] = useState(false);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);

  const handleLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    if (naturalWidth > 0 && naturalHeight > 0) {
      setAspectRatio(naturalWidth / naturalHeight);
    }
  };

  if (hasError) {
    return (
      <div className="p-3 w-full border rounded-md border-tertiary flex items-center justify-center">
        <p className="max-w-[700px] text-body-2 text-tertiary text-center">
          {t($ => $.failedToLoad, {
            alt,
          })}
        </p>
      </div>
    );
  }

  const image = (
    <Image
      src={src}
      width={0}
      height={0}
      alt={alt}
      className={cx('object-contain w-full h-auto max-w-full', { 'rotate-180': rotation === 180 })}
      ref={ref}
      data-dd-privacy="mask"
      onError={() => setHasError(true)}
      onLoad={handleLoad}
    />
  );

  if (isSideways(rotation) && aspectRatio !== null) {
    return (
      <div
        className="relative w-full overflow-hidden border rounded shadow-md bg-primary border-tertiary"
        style={{ aspectRatio: 1 / aspectRatio }}
      >
        <div
          className={cx('absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2', ROTATION_CLASS_NAME[rotation])}
          style={{ width: `${aspectRatio * 100}%` }}
        >
          {image}
        </div>
      </div>
    );
  }

  return <div className="overflow-hidden border rounded shadow-md bg-primary border-tertiary">{image}</div>;
});

export default BaseImage;
